import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserIcon, LogOutIcon, XIcon } from 'lucide-react';
import { navGroups } from '../data/navigation';
import { View } from '../types/view';
interface SidebarProps {
  currentView: View;
  onNavigate: (view: View) => void;
  isAdmin: boolean;
  username: string;
  userRole: string;
  onLogout: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** Detail views that should keep their parent nav item highlighted. */
const parentView: Partial<Record<View, View>> = {
  'quality-detail': 'quality',
  'task-detail': 'tasks',
  'customer-detail': 'customers'
};
export function Sidebar({
  currentView,
  onNavigate,
  isAdmin,
  username,
  userRole,
  onLogout,
  isMobileOpen,
  onMobileClose
}: SidebarProps) {
  const content = <div className="flex h-full flex-col bg-white" data-id="element-743" data-mp="3cbd27x13achf">
      {/* Brand */}
      <div className="flex items-center justify-between gap-3 px-5 py-5" data-id="element-744" data-mp="37eeb7ncq2lng">
        <div className="flex items-center gap-2.5" data-id="element-745" data-mp="1lz5ejlka24jd">
          <span className="text-xl font-bold tracking-tight text-stone-900" data-id="element-746" data-mp="1lxkkdx1vknm1">
            Lykke-EL
          </span>
          <span className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-500" data-id="element-747" data-mp="1qs8dyabqk87h">
            {userRole}
          </span>
        </div>
        <button onClick={onMobileClose} className="-mr-1 rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 lg:hidden" aria-label="Luk menu" data-id="element-748" data-mp="1h1dlgo7wccdl">
          <XIcon className="h-5 w-5" data-id="element-749" data-mp="29n5g7gj4nfcq" />
        </button>
      </div>

      {/* Navigation */}
      <nav aria-label="Hovednavigation" className="flex-1 overflow-y-auto px-3 pb-4" data-id="element-750" data-mp="1vp75cl40m11z">
        {navGroups.map(group => {
        const items = group.items.filter(item => !item.adminOnly || isAdmin);
        if (items.length === 0) return null;
        return <div key={group.label ?? 'main'} className="mb-1" data-id="element-751" data-mp="papozph63v69">
              {group.label && <h2 className="px-3 pb-1 pt-5 text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-752" data-mp="2qxsk134q9no2">
                  {group.label}
                </h2>}
              <ul className="space-y-0.5" data-id="element-753" data-mp="2h7g1thkdy6l3">
                {items.map(item => {
              const Icon = item.icon;
              const isActive = currentView === item.id || parentView[currentView] === item.id;
              return <li key={item.id} data-id="element-754" data-mp="237y6ejcl3v2r">
                      <button onClick={() => onNavigate(item.id)} aria-current={isActive ? 'page' : undefined} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${isActive ? 'bg-brand-50 text-brand-700' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'}`} data-id="element-755" data-mp="23b2h7zkk1p2e">
                        <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${isActive ? 'text-brand-600' : 'text-stone-400'}`} data-id="element-756" data-mp="1fd4lpwhiffdu" />
                        <span className="flex-1 truncate" data-id="element-757" data-mp="1o4ogy8mxv742">{item.label}</span>
                        {item.badge && <span className="flex-shrink-0 rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand-700" data-id="element-758" data-mp="rhjqhz2bxhbq">
                            {item.badge}
                          </span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>;
      })}
      </nav>

      {/* Account */}
      <div className="border-t border-stone-200 p-3" data-id="element-759" data-mp="2bxwr3k12eeow">
        <button onClick={() => onNavigate('profile')} aria-current={currentView === 'profile' ? 'page' : undefined} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${currentView === 'profile' ? 'bg-brand-50' : 'hover:bg-stone-100'}`} data-id="element-760" data-mp="2gtc4ybz2oalb">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-stone-900 text-xs font-semibold uppercase text-white" data-id="element-761" data-mp="2kc1hl2qx37or">
            {username.slice(0, 2)}
          </span>
          <span className="min-w-0 flex-1" data-id="element-762" data-mp="2te10k0lhsluc">
            <span className="block truncate text-sm font-medium text-stone-900" data-id="element-763" data-mp="2tczel12xuexu">
              Profil
            </span>
            <span className="block truncate text-xs text-stone-500" data-id="element-764" data-mp="383ylm7o4kvfb">
              {username}
            </span>
          </span>
          <UserIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-765" data-mp="3h2ae6f8jy3ae" />
        </button>
        <button onClick={onLogout} className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-stone-500 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-766" data-mp="3c40m4di4jit0">
          <LogOutIcon className="h-[18px] w-[18px] flex-shrink-0" data-id="element-767" data-mp="4hwofv6j0k97" />
          Log ud
        </button>
      </div>
    </div>;
  return <>
      {/* Desktop */}
      <aside className="hidden w-64 flex-shrink-0 border-r border-stone-200 lg:block" data-id="element-768" data-mp="3qpwl3w40g2pp">
        <div className="sticky top-0 h-screen" data-id="element-769" data-mp="3cz15m0z0and5">{content}</div>
      </aside>

      {/* Mobile slide-over */}
      <AnimatePresence data-id="element-770" data-mp="3hphoi0llj16w">
        {isMobileOpen && <div className="lg:hidden" data-id="element-771" data-mp="3hr2fxhcm640j">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} onClick={onMobileClose} className="fixed inset-0 z-40 bg-stone-900/40" data-id="element-772" data-mp="3314xzj5a2ajs" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.26,
          ease: EASE
        }} className="fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] overflow-hidden border-r border-stone-200 pb-16 shadow-xl" data-id="element-773" data-mp="2yzrsxba0ebdx">
              {content}
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}